// Phase COTTON-RM-002 Part B — Bale QR Scan page.
//
// Mobile-first: huge input field, big result card, context selector,
// scan history. Camera-scanner hook is a placeholder — the input field
// is the production-ready fallback.

import { useRef, useState } from 'react'
import {
  Alert, App, Button, Card, Col, Empty, Input, List, Row, Select, Space,
  Tag, Typography,
} from 'antd'
import {
  CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined,
  QuestionCircleOutlined, ScanOutlined, WarningOutlined,
} from '@ant-design/icons'
import { useMutation } from '@tanstack/react-query'

import {
  cottonRmApi, type QRScanResult, SCAN_CONTEXTS, type ScanContext,
} from '@/features/cotton-rm/cottonRmApi'

const { Text, Title } = Typography

function resultColor(r: string) {
  if (r === 'ALLOWED') return 'success'
  if (r === 'WARNING') return 'warning'
  if (r === 'BLOCKED') return 'error'
  if (r === 'OVERRIDE_REQUIRED') return 'warning'
  return 'default'
}

function resultIcon(r: string) {
  if (r === 'ALLOWED') return <CheckCircleOutlined />
  if (r === 'WARNING') return <WarningOutlined />
  if (r === 'BLOCKED') return <CloseCircleOutlined />
  if (r === 'OVERRIDE_REQUIRED') return <ExclamationCircleOutlined />
  return <QuestionCircleOutlined />
}

export default function BaleScanPage() {
  const { message } = App.useApp()
  const [scanContext, setScanContext] = useState<ScanContext>('STOCK_AUDIT')
  const [qrInput, setQrInput] = useState('')
  const [last, setLast] = useState<QRScanResult | null>(null)
  const [history, setHistory] = useState<QRScanResult[]>([])
  const inputRef = useRef<HTMLInputElement>(null)

  const scanMut = useMutation({
    mutationFn: () => cottonRmApi.scan({
      qr_code_value: qrInput.trim(),
      scan_context: scanContext,
      source_module: 'WEB_SCAN_PAGE',
    }),
    onSuccess: (r) => {
      setLast(r)
      setHistory(h => [r, ...h].slice(0, 50))
      setQrInput('')
      // Re-focus for the next scan (gun input typically posts ENTER)
      setTimeout(() => inputRef.current?.focus(), 100)
      if (r.result_status === 'BLOCKED') {
        message.error(r.error_message || 'Scan blocked')
      } else if (r.result_status === 'UNKNOWN_QR') {
        message.error('Unknown QR — not in this database')
      }
    },
    onError: (e: Error) => message.error(e.message),
  })

  return (
    <div style={{ padding: 16, maxWidth: 1200, margin: '0 auto' }}>
      <Title level={2} style={{ marginTop: 0 }}>Bale QR Scan</Title>
      <Text type="secondary">
        Scan the QR sticker on a bale, or type its value manually. Every
        scan is recorded and validated against the selected context.
      </Text>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} md={14}>
          <Card title="Scan">
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div>
                <Text strong>Context</Text>
                <Select value={scanContext} onChange={setScanContext}
                        style={{ width: '100%', marginTop: 4 }}
                        options={SCAN_CONTEXTS.map(c => ({ value: c, label: c }))} />
              </div>
              <div>
                <Text strong>QR or Bale code</Text>
                <Input ref={inputRef as never}
                        autoFocus
                        value={qrInput}
                        onChange={e => setQrInput(e.target.value)}
                        onPressEnter={() => qrInput.trim() && scanMut.mutate()}
                        placeholder="UCL:BALE:PLANT:BALE-001"
                        size="large"
                        prefix={<ScanOutlined />}
                        style={{ fontSize: 18, marginTop: 4 }} />
              </div>
              <Button type="primary" size="large" block
                       icon={<ScanOutlined />}
                       loading={scanMut.isPending}
                       disabled={!qrInput.trim()}
                       onClick={() => scanMut.mutate()}>
                Scan
              </Button>
              <Alert type="info" showIcon
                      message="Camera scanner hook is reserved. Use a scan gun or paste the QR value here. Press Enter to submit." />
            </Space>
          </Card>

          {last && (
            <Card style={{ marginTop: 12 }}
                   title={
                     <Space>
                       {resultIcon(last.result_status)}
                       <Tag color={resultColor(last.result_status)}>
                         {last.result_status}
                       </Tag>
                       <Text>{last.scan_context}</Text>
                     </Space>
                   }>
              {last.error_message && (
                <Alert type={last.result_status === 'WARNING' ? 'warning' : 'error'}
                        showIcon style={{ marginBottom: 12 }}
                        message={last.error_message} />
              )}
              {last.bale ? (
                <Row gutter={[12, 8]}>
                  <Col span={12}><Text type="secondary">Bale</Text>
                    <div><Text strong>{last.bale.bale_no}</Text></div></Col>
                  <Col span={12}><Text type="secondary">Status</Text>
                    <div><Tag>{last.bale.status}</Tag></div></Col>
                  <Col span={12}><Text type="secondary">QC</Text>
                    <div><Tag>{last.bale.qc_status}</Tag></div></Col>
                  <Col span={12}><Text type="secondary">Stage</Text>
                    <div><Tag>{last.bale.bale_stage}</Tag></div></Col>
                  <Col span={12}><Text type="secondary">Godown</Text>
                    <div>{last.bale.godown ?? '—'}</div></Col>
                  <Col span={12}><Text type="secondary">Weight</Text>
                    <div>{last.bale.weight_kg ?? '—'} kg</div></Col>
                  <Col span={12}><Text type="secondary">₹ / kg</Text>
                    <div>{last.bale.effective_cost_per_kg
                            ? `₹${last.bale.effective_cost_per_kg}` : '—'}</div></Col>
                  <Col span={24}><Text type="secondary">QR</Text>
                    <div><code>{last.qr_code_value}</code></div></Col>
                </Row>
              ) : (
                <Alert type="error" showIcon
                        message="QR did not resolve to any bale." />
              )}
            </Card>
          )}
        </Col>

        <Col xs={24} md={10}>
          <Card title={`Recent scans (${history.length})`}>
            {history.length === 0 ? <Empty description="No scans yet." /> : (
              <List size="small"
                     dataSource={history}
                     renderItem={(r) => (
                       <List.Item>
                         <Space style={{ width: '100%' }} direction="vertical" size={2}>
                           <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                             <Tag color={resultColor(r.result_status)}>
                               {r.result_status}
                             </Tag>
                             <Text type="secondary" style={{ fontSize: 11 }}>
                               {new Date(r.scan_datetime).toLocaleTimeString()}
                             </Text>
                           </Space>
                           <Text style={{ fontSize: 12 }}>
                             {r.scan_context} · {r.bale?.bale_no ?? r.qr_code_value}
                           </Text>
                         </Space>
                       </List.Item>
                     )} />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  )
}
